import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, ArrowLeft, Sparkles, FileDown, Award } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BadgeCard from '@/components/BadgeCard';
import { Button } from '@/components/ui/button';
import { getBadges } from '@/utils/api';
import { getBadgesForLanguage } from '@/data/badgeCatalog';
import { getCertConfig, generateCertificatePDF, isCertifiableBadge } from '@/utils/certificateGenerator';
import { useCourseLanguage, getLangInfo } from '@/data/languages';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const Badges = () => {
  const navigate = useNavigate();
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const learnerName = localStorage.getItem('studentName') || 'Learner';
  const [lang] = useCourseLanguage();
  const ALL_BADGES = getBadgesForLanguage(lang);

  useEffect(() => {
    const fetchBadges = async () => {
      const badges = await getBadges();
      setEarnedBadges(badges);
      setLoading(false);
    };
    fetchBadges();
  }, []);

  const earnedIds = new Set(earnedBadges.map(b => b.badge_id));
  const earnedCount = ALL_BADGES.filter(b => earnedIds.has(b.id)).length;
  const totalCount = ALL_BADGES.length;
  const progressPct = Math.round((earnedCount / totalCount) * 100);

  // Certificates available (earned & certifiable)
  const availableCertificates = earnedBadges
    .filter(b => isCertifiableBadge(b.badge_id))
    .map(b => ({ badgeId: b.badge_id, earnedAt: b.earned_at, ...getCertConfig(b.badge_id) }));

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />
      <main data-testid="badges-container" className="pt-24 pb-12 max-w-7xl mx-auto px-6 w-full flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(255,184,0,0.4)]">
            <Trophy className="w-10 h-10 text-black" />
          </div>
          <h1 className="font-heading text-5xl font-bold text-white mb-3">Your Badges</h1>
          <p className="text-[#A1A1AA] font-body mb-5">Earn badges by mastering concepts and DSA problems</p>
          <LanguageSwitcher testIdPrefix="badges-lang" />
          <div className="text-[#71717A] text-xs font-code mt-2" data-testid="badges-course-label">Showing {getLangInfo(lang).label} course badges</div>

          {/* Progress */}
          <div className="mt-6 max-w-md mx-auto">
            <div className="flex justify-between items-center mb-2 text-sm font-code">
              <span className="text-[#71717A]">Progress</span>
              <span className="text-[#FFB800] font-bold">{earnedCount} / {totalCount}</span>
            </div>
            <div className="h-3 bg-[#1A1A20] rounded-full overflow-hidden border border-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-[#FFB800] to-[#FF8800] shadow-[0_0_20px_rgba(255,184,0,0.5)]"
              />
            </div>
            <div className="text-[#71717A] text-xs font-code mt-2">
              {progressPct}% complete · Keep going, {learnerName}!
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="text-center text-[#71717A] py-20 font-body">Loading badges...</div>
        ) : (
          <>
            {/* Certificates Section */}
            {availableCertificates.length > 0 && (
              <div data-testid="certificates-section" className="mb-12">
                <h2 className="font-heading text-2xl font-semibold text-white mb-2 flex items-center gap-2">
                  <Award className="w-6 h-6 text-[#FFB800]" />
                  Your Certificates
                </h2>
                <p className="text-[#71717A] text-sm font-body mb-6">
                  Downloadable PDF certificates for your highest achievements
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {availableCertificates.map((cert, i) => {
                    const [r, g, b] = cert.accentColor;
                    const [r2, g2, b2] = cert.accentColor2;
                    const rgb = `rgb(${r}, ${g}, ${b})`;
                    const rgb2 = `rgb(${r2}, ${g2}, ${b2})`;
                    return (
                      <motion.div
                        key={cert.badgeId}
                        data-testid={`certificate-card-${cert.badgeId}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="relative rounded-xl p-6 border overflow-hidden"
                        style={{
                          background: `linear-gradient(135deg, #0F0F12 60%, ${rgb}15)`,
                          borderColor: rgb + '55'
                        }}
                      >
                        {/* Corner ornament */}
                        <div 
                          className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-40"
                          style={{ background: rgb }}
                        />
                        
                        {/* Mini certificate preview */}
                        <div className="relative bg-[#050505] border-2 rounded p-3 mb-4"
                          style={{ borderColor: rgb }}
                        >
                          <div className="text-center space-y-1 py-2">
                            <div className="text-[8px] font-code tracking-widest uppercase" style={{ color: rgb2 }}>
                              Certificate of
                            </div>
                            <div className="text-xs font-heading font-bold text-white">
                              {cert.title.replace('Certificate of ', '')}
                            </div>
                            <div className="text-[8px] font-code" style={{ color: rgb2 }}>{getLangInfo(cert.language).label.toUpperCase()}</div>
                            <div className="h-px my-1" style={{ background: rgb + '55' }} />
                            <div className="text-[9px] italic text-[#A1A1AA]">
                              awarded to
                            </div>
                            <div className="text-sm font-heading font-bold text-white truncate px-1">
                              {learnerName}
                            </div>
                            <div className="text-[7px] font-code text-[#71717A] pt-1">
                              DSA WITH KAPIL
                            </div>
                          </div>
                        </div>

                        <div className="relative">
                          <h3 className="font-heading text-lg font-bold text-white mb-1">
                            {cert.subtitle}
                          </h3>
                          <p className="text-xs text-[#A1A1AA] font-body mb-4 min-h-[36px]">
                            Earned on {new Date(cert.earnedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>

                          <Button
                            data-testid={`certificate-download-${cert.badgeId}`}
                            onClick={() => generateCertificatePDF(cert.badgeId, learnerName, cert.earnedAt, localStorage.getItem('learnerId'))}
                            className="w-full font-heading font-semibold text-sm text-white"
                            style={{ 
                              background: `linear-gradient(to right, ${rgb}, ${rgb2})`,
                              boxShadow: `0 4px 20px ${rgb}44`
                            }}
                          >
                            <FileDown className="w-4 h-4 mr-2" />
                            Download PDF Certificate
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="mt-10 flex items-center gap-4">
                  <div className="h-px flex-1 bg-white/10" />
                  <div className="text-[#71717A] text-xs font-code uppercase tracking-widest">All Badges</div>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
              </div>
            )}

            {/* Earned Badges */}
            {earnedCount > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FFB800]" />
                  Earned ({earnedCount})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {ALL_BADGES.filter(b => earnedIds.has(b.id)).map((b, i) => (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <BadgeCard badge={{ badge_id: b.id }} learnerName={learnerName} earned={true} />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Badges */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                🔒 Locked ({totalCount - earnedCount})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {ALL_BADGES.filter(b => !earnedIds.has(b.id)).map(b => (
                  <BadgeCard key={b.id} badge={{ badge_id: b.id }} learnerName={learnerName} earned={false} />
                ))}
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Badges;
